import React, { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';

/**
 * HONG&CHEON Corporate Website
 * 브랜드 컬러: HONG& (#77D2FB), CHEON (#F279AE)
 * 수정 사항: 푸터에 사업자 번호 및 주소 정보 추가
 */

// --- 공통 컴포넌트: 로고 ---
const BrandLogo = () => (
  <div className="flex items-center gap-3 text-2xl font-black italic tracking-tighter text-slate-900">
    <img 
      src="https://github.com/hongcheon95/TestImages/blob/main/HNC_Sticker_Round_48x48.png?raw=true" 
      alt="HONG&CHEON Logo" 
      className="w-8 h-8 md:w-9 md:h-9 object-contain"
    />
    <div>
      <span className="text-[#77D2FB]">HONG&</span>
      <span className="text-[#F279AE]">CHEON</span>
    </div>
  </div>
);

// --- 공통 컴포넌트: 푸터 ---
const Footer = () => (
  <footer className="py-24 px-8 border-t border-slate-100 text-center bg-white text-slate-400">
    <div className="mb-8 flex justify-center">
      <BrandLogo />
    </div>
    <div className="space-y-6 max-w-2xl mx-auto">
      <p className="text-sm md:text-base font-bold text-slate-800 tracking-tight">
        API VISION AI S/W Beyond the Pixels.
      </p>
      
      <div className="flex flex-col items-center space-y-4">
        <a 
          href="mailto:hongcheon95@gmail.com" 
          className="text-sm text-[#77D2FB] hover:text-[#F279AE] transition-colors font-medium flex items-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
          hongcheon95@gmail.com
        </a>

        {/* 기업 정보 추가 영역 */}
        <div className="text-[11px] md:text-xs text-slate-400 font-light leading-relaxed">
          <p>사업자 번호 : 298-13-01396</p>
          <p>주소 : 경상북도 안동시 합전 2길 13, 2층</p>
        </div>
      </div>

      <p className="text-[10px] uppercase tracking-[0.5em] font-medium mt-12 pt-8 border-t border-slate-50 opacity-30">
        &copy; {new Date().getFullYear()} HONG&CHEON Corp. All Rights Reserved.
      </p>
    </div>
  </footer>
);

// --- 서브 컴포넌트: HOME 컨텐츠 ---
const HomeContent = ({ containerRef }) => {
  const zoomSectionRef = useRef(null);
  const mottoSectionRef = useRef(null);

  const { scrollYProgress: zoomProgress } = useScroll({
    container: containerRef,
    target: zoomSectionRef,
    offset: ["start start", "end end"]
  });

  const { scrollYProgress: mottoProgress } = useScroll({
    container: containerRef,
    target: mottoSectionRef,
    offset: ["start start", "end end"]
  });

  const smoothZoom = useSpring(zoomProgress, { stiffness: 100, damping: 30 });
  const smoothMotto = useSpring(mottoProgress, { stiffness: 100, damping: 30 });

  const pixelScale = useTransform(smoothZoom, [0, 0.85], [1, 600]);
  const pixelOpacity = useTransform(smoothZoom, [0.8, 1], [1, 0]); 

  const textOpacity = useTransform(smoothMotto, [0.05, 0.2, 0.8, 0.95], [0, 1, 1, 0]);
  const textY = useTransform(smoothMotto, [0.05, 0.2], [40, 0]);
  const textScale = useTransform(smoothMotto, [0.05, 0.2], [0.95, 1]);

  const processSteps = [
    { title: "개발 요구사항 확인", desc: "고객사로부터 개발 요구사항을 전달 받습니다. 명확한 비전이 없어도 조율을 통해 방향을 정립합니다.", icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg> },
    { title: "검토 및 회의", desc: "내부 검토를 통해 추가 필요 사항과 확인 사항을 개발진과 함께 정리합니다.", icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg> },
    { title: "고객사와 미팅 및 조율", desc: "여러 차례의 미팅을 통해 설치 환경, 정확도, 검출 객체 등을 명확히 확인하고 조율합니다.", icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><polyline points="16 11 18 13 22 9"></polyline></svg> },
    { title: "전체 개발 기획서 송부", desc: "전체적인 개발 내용이 정리된 개발 기획서와 견적서를 송부 드립니다.", icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg> }
  ];

  return (
    <div className="snap-y snap-proximity">
      {/* 1. 픽셀 줌 섹션 */}
      <div ref={zoomSectionRef} className="h-[200vh] relative">
        <section className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #77D2FB 0%, #ffffff 50%, #F279AE 100%)', backgroundSize: '200% 200%', animation: 'gradientMove 15s ease infinite' }} />
          <motion.div 
            style={{ scale: pixelScale, opacity: pixelOpacity, boxShadow: '0 0 50px rgba(255,255,255,0.8)' }} 
            className="w-12 h-12 bg-white rounded-sm relative z-10 flex items-center justify-center"
          >
            <div className="absolute inset-0 bg-white blur-2xl opacity-40 scale-150 animate-pulse" />
          </motion.div>
          <motion.div style={{ opacity: useTransform(zoomProgress, [0, 0.1], [1, 0]) }} className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-slate-400 text-center">
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-slate-500">Scroll to Dive</span>
            <div className="w-px h-16 bg-gradient-to-b from-slate-400 to-transparent"></div>
          </motion.div>
        </section>
      </div>

      {/* 2. 모토 리빌 섹션 */}
      <div ref={mottoSectionRef} className="h-[200vh] relative bg-white snap-start">
        <section className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden border-b border-slate-50">
          <motion.div style={{ opacity: textOpacity, y: textY, scale: textScale }} className="text-center px-6 max-w-6xl flex flex-col items-center">
            <h1 className="text-4xl md:text-6xl font-black mb-12 tracking-tighter leading-tight text-slate-900">
              <span className="bg-gradient-to-r from-[#77D2FB] via-[#8ec5fc] to-[#F279AE] bg-clip-text text-transparent">픽셀 너머의 가치를 찾아서</span>
            </h1>
            <div className="space-y-10 text-slate-800">
              <p className="text-base md:text-xl font-light leading-relaxed text-slate-500 break-keep">사람은 보지 못하는, 혹은 보기에는 너무 방대한 데이터.</p>
              <p className="text-lg md:text-3xl font-medium leading-tight break-keep">우리는 <span className="font-black text-[#77D2FB] relative inline-block px-2">Vision AI<motion.span initial={{ width: 0 }} whileInView={{ width: '100%' }} className="absolute bottom-1 left-0 h-3 bg-[#F279AE] opacity-30 -z-10 rounded-full" /></span>를 통해 <br className="hidden md:block" /> 그 한계를 넘어서고자 합니다.</p>
            </div>
          </motion.div>
        </section>
      </div>

      {/* 3. 개발 프로세스 섹션 */}
      <section id="process" className="relative z-30 bg-white py-40 px-8 border-t border-slate-50 snap-start">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tighter text-slate-900 uppercase">Development Process</h2>
            <p className="text-slate-500 text-lg font-light tracking-tight">HONG&CHEON이 제공하는 투명하고 체계적인 협업 프로세스입니다.</p>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#77D2FB] to-[#F279AE] -translate-x-1/2 opacity-20" />
            <div className="space-y-24 relative">
              {processSteps.map((step, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: index * 0.1 }} className={`flex flex-col md:flex-row items-center gap-10 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                  <div className="flex-1 text-center md:text-left">
                    <div className={`flex flex-col ${index % 2 === 1 ? 'md:items-end md:text-right' : 'md:items-start'}`}>
                      <span className="text-xs font-black tracking-widest text-[#77D2FB] uppercase mb-2 tracking-[0.3em]">Step 0{index + 1}</span>
                      <h3 className="text-2xl md:text-3xl font-bold mb-4 text-slate-900 leading-tight">{step.title}</h3>
                      <p className="text-slate-500 leading-relaxed font-light break-keep max-w-lg">{step.desc}</p>
                    </div>
                  </div>
                  <div className="relative z-10 w-16 h-16 rounded-full bg-white border-4 border-slate-50 flex items-center justify-center text-white shadow-xl">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${index % 2 === 0 ? 'bg-[#77D2FB]' : 'bg-[#F279AE]'}`}>
                        {step.icon}
                      </div>
                  </div>
                  <div className="hidden md:block flex-1" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Contact Us 섹션 */}
      <section id="contact" className="relative z-30 bg-slate-50 py-40 px-8 snap-start">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-5xl font-black mb-8 tracking-tighter text-slate-900 uppercase">Contact Us</h2>
            <p className="text-slate-500 text-lg md:text-xl font-light mb-12 break-keep">
              Vision AI 솔루션에 대한 궁금증이나 프로젝트 협업 제안은 <br className="hidden md:block" /> 
              아래 메일로 언제든 편하게 연락 부탁드립니다.
            </p>
            
            <div className="inline-block group relative">
              <a 
                href="mailto:hongcheon95@gmail.com" 
                className="text-3xl md:text-5xl lg:text-7xl font-black tracking-tighter text-slate-900 hover:text-[#77D2FB] transition-all duration-300 break-all"
              >
                hongcheon95<span className="text-[#F279AE]">@</span>gmail.com
              </a>
              <div className="h-2 w-full bg-[#77D2FB] absolute bottom-2 left-0 -z-10 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 opacity-20" />
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// --- 서브 컴포넌트: SOLUTIONS 컨텐츠 ---
const SolutionsContent = () => {
  const historyData = [
    {
      year: "2023",
      items: [
        "손톱 영역 검출 프로그램 개발",
        "작업자 인식 및 추적 시스템 개발",
        "논산 Cookit ECS System 추가 개발",
        "차량 번호 인식 고도화 시스템 개발",
        "애완동물 비문 인식 시스템 개발",
        "실시간 동영상에서 배경 제거 프로그램 개발"
      ]
    },
    {
      year: "2022",
      items: [
        "3D 프린팅 객체의 출력 오류 검출 및 알람 프로그램 개발",
        "핫도그 멀티 포장지 일부인 검사",
        "핫도그 일매 포장기 자동화 프로젝트",
        "핫도그 성형 자동화 프로젝트",
        "저작 행위 인식 프로그램 개발",
        "차량 번호 인식 프로젝트"
      ]
    },
    {
      year: "2021",
      items: [
        "영상처리 기반의 수건 끝단 검출 추가 모듈 개발",
        "인공지능 기반의 CCTV DATA 상의 차량 카운팅 프로그램 개발",
        "CCTV를 통한 학교 폭력 인식 프로그램 개발",
        "LG TROMM 비전 검사 개발",
        "2D Hand Pose Estimation 및 2D Point Tracking 개발",
        "OAK-D를 이용한 AprilTag 검출 프로그램 개발",
        "DashCam 기반의 ADAS APK 개발",
        "VIA2기반 라벨링 툴 프로그램 개발",
        "Polar-FFT 자동화 알고리즘 개발 및 이미지 처리를 위한 GUI 툴 개발",
        "논산 Cookit ECS System 개발",
        "핫도그 제조 공정 불량 판정 및 제품 정보 판단 프로그램 개발"
      ]
    },
    {
      year: "2020",
      items: [
        "인공지능 기반의 실시간 교통량 측정 프로그램 개발",
        "HDF5 스펙트럼 Peak 검출 및 정렬 프로그램 개발",
        "인공지능 기반 실시간 처리가 가능한 야간 자동차 인식 프로그램 개발",
        "인공지능 기반 눈썹 검출 및 자동 합성 프로그램 개발",
        "인공지능 기반 실시간 처리가 가능한 공 인식 및 공 궤적 추적 시스템 개발",
        "인공지능 기반 작업자 동작 분석 기술 개발",
        "열 화상 카메라 환경의 다양한 객체 검출 프로그램 개발",
        "실시간 처리가 가능한 음식 유통기한 예측 프로그램 개발",
        "동공 내의 White Dot 검출 프로그램 개발",
        "YoloV3 모델을 기반으로 한 도보 상의 사람 인식 및 이동 방향 판단, 추적 시스템 개발",
        "인공지능 기반의 메이크업 가이드 스마트 미러(Smart Mirror) 시스템 개발",
        "인공지능 기반 공정 물품(반도체, 주요 부속 부품 등)의 객체 검출 및 분류 시스템 개발",
        "회귀 분석을 이용한 Spectrum Drift 선형 정렬 프로그램 개발",
        "인공지능 기반의 수건 공장 자동화 시스템 개발 프로토타입 개발",
        "도보 위 사람들의 행동 인식 시스템 개발",
        "중장비 접근 알림 프로그램 개발",
        "인공지능 기반의 홍수, 침수 예측 프로그램 개발",
        "인공지능 기반의 배경 검출 및 배경 합성 프로그램 개발"
      ]
    },
    {
      year: "2019.12",
      items: [
        "지능형 Edge 솔루션을 위한 딥러닝 기반 비디오 객체 인식 서비스 테스트베드 구축 및 시뮬레이터 구현",
        "딥러닝 기반 실시간 콘덴서 불량 검출 프로그램 개발",
        "HDF5 스펙트럼 이미지 데이터 처리 및 보정 프로그램",
        "HDF5 스펙트럼 이미지 보정 영역 검출 프로그램",
        "머신러닝 기반 공정 물품 일련번호 분류 프로그램 개발",
        "딥러닝 기반 실시간 번호판 인식 및 보정 프로그램 개발"
      ]
    }
  ];

  return (
    <div className="pt-32 pb-20 bg-slate-50/30">
      <header className="max-w-7xl mx-auto px-8 mb-24">
        <span className="text-xs font-black tracking-[0.4em] text-[#F279AE] uppercase mb-4 block">Our Portfolio</span>
        <h2 className="text-5xl md:text-7xl font-black tracking-tighter text-slate-900 leading-none">
          Proven <br/><span className="text-[#77D2FB]">Technology.</span>
        </h2>
      </header>

      {/* Main Tech Showcase 1: Segmentation */}
      <section className="max-w-7xl mx-auto px-8 mb-40">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8 order-2 lg:order-1">
            <h3 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight">High-Precision Segmentation</h3>
            <p className="text-xl text-slate-500 font-light leading-relaxed break-keep">머리카락과 같은 극도로 세밀한 영역까지 실시간으로 분리해내는 AI 모델입니다. 고해상도 이미지에서도 디테일의 손실 없이 완벽한 마스킹을 구현합니다.</p>
            <div className="flex flex-wrap gap-4">
              {["Segmentation", "High-Res", "Vision AI"].map(tag => (
                <span key={tag} className="text-xs font-bold text-slate-400 border border-slate-200 px-4 py-2 rounded-full">#{tag}</span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 order-1 lg:order-2">
            <div className="relative rounded-3xl overflow-hidden shadow-xl border border-slate-100">
              <img src="https://github.com/hongcheon95/TestImages/blob/main/16617208122_d49f100f58_o.jpg?raw=true" className="w-full aspect-video object-cover" alt="Original" />
              <div className="absolute top-6 left-6 px-4 py-2 bg-black/40 backdrop-blur-md rounded-full text-white text-[10px] font-black uppercase tracking-widest border border-white/20">Original</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-white border border-slate-100">
              <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <img src="https://github.com/hongcheon95/TestImages/blob/main/16617208122_d49f100f58_o_rgba.png?raw=true" className="relative z-10 w-full aspect-video object-cover drop-shadow-2xl" alt="H&C Result" />
              <div className="absolute top-6 left-6 z-20 px-4 py-2 bg-[#77D2FB] rounded-full text-white text-[10px] font-black uppercase tracking-widest">H&C Result</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Tech Showcase 2: Food Process Inspection */}
      <section className="max-w-7xl mx-auto px-8 mb-40">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative rounded-[3rem] overflow-hidden shadow-2xl border border-slate-100 bg-white group p-4 lg:p-8">
             <img 
                src="https://github.com/hongcheon95/TestImages/blob/main/%5B%ED%99%8D%EC%95%A4%EC%B2%9C_%EC%A3%BC%EC%9A%94%EC%8B%A4%EC%A0%81%5D2022-07-20.png?raw=true" 
                className="w-full h-auto rounded-2xl drop-shadow-xl" 
                alt="Food Process Inspection System"
             />
          </div>

          <div className="space-y-8">
            <h3 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight break-keep">Smart Food Process Inspection</h3>
            <div className="space-y-6">
                <p className="text-xl text-slate-500 font-light leading-relaxed break-keep">
                    식품 제조 공정에서 불량을 판정하고 제품의 다양한 정보를 동시에 판단하는 지능형 시스템입니다. 
                </p>
                <div className="p-8 rounded-3xl bg-white border border-slate-100 shadow-sm">
                    <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <span className="text-[#F279AE]">●</span> 엣지 포인트 검사 방식 도입
                    </h4>
                    <p className="text-slate-500 font-light leading-relaxed break-keep">
                        시스템의 가장 큰 특징은 **9개의 검사 포인트**에서 개별적으로 정밀 검사를 실시한 후, 
                        수집된 데이터를 기반으로 최종 결과를 한 번에 확인할 수 있다는 점입니다. 이를 통해 검사의 누락을 방지하고 정확도를 극대화했습니다.
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {["Defect Judgment", "Edge Point", "Factory Automation"].map(tag => (
                <span key={tag} className="text-xs font-bold text-slate-400 border border-slate-200 px-4 py-2 rounded-full">#{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 개발 연혁 섹션 */}
      <section className="py-32 px-8 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <h4 className="text-4xl font-black text-slate-900 tracking-tighter uppercase mb-4">Development History</h4>
            <div className="w-24 h-1 bg-gradient-to-r from-[#77D2FB] to-[#F279AE]" />
          </div>

          <div className="space-y-12">
            {historyData.map((yearGroup, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="flex flex-col md:flex-row gap-8 md:gap-16 border-b border-slate-50 pb-12 last:border-0"
              >
                <div className="md:w-32 flex-shrink-0">
                  <span className="text-3xl md:text-4xl font-black text-slate-900 italic tracking-tighter">
                    {yearGroup.year}
                  </span>
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                  {yearGroup.items.map((item, i) => (
                    <div key={i} className="flex gap-3 group">
                      <span className="text-[#77D2FB] font-black group-hover:scale-125 transition-transform">•</span>
                      <p className="text-slate-600 font-light text-sm md:text-base leading-relaxed break-keep group-hover:text-slate-900 transition-colors">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// --- 메인 APP ---
const App = () => {
  const [view, setView] = useState('home');
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
  }, [view]);

  return (
    <div 
      ref={containerRef}
      className="relative bg-white font-sans h-screen overflow-y-auto overflow-x-hidden selection:bg-[#F279AE] selection:text-white"
    >
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-[100] px-8 py-5 flex justify-between items-center bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm text-slate-900">
        <div className="cursor-pointer" onClick={() => setView('home')}>
          <BrandLogo />
        </div>
        <div className="flex space-x-12 text-xs font-bold uppercase tracking-[0.2em] text-slate-600">
          <button 
            onClick={() => setView('home')}
            className={`transition-all duration-300 pb-1 ${view === 'home' ? 'text-[#77D2FB] border-b-2 border-[#77D2FB]' : 'hover:text-[#77D2FB]'}`}
          >
            HOME
          </button>
          <button 
            onClick={() => setView('solutions')}
            className={`transition-all duration-300 pb-1 ${view === 'solutions' ? 'text-[#F279AE] border-b-2 border-[#F279AE]' : 'hover:text-[#F279AE]'}`}
          >
            SOLUTIONS
          </button>
        </div>
      </nav>

      <AnimatePresence mode="wait">
        {view === 'home' ? (
          <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <HomeContent containerRef={containerRef} />
          </motion.div>
        ) : (
          <motion.div key="solutions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <SolutionsContent />
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        html { scroll-behavior: smooth; }
        body { -webkit-font-smoothing: antialiased; }
        .break-keep { word-break: keep-all; }
      `}</style>
    </div>
  );
};

export default App;